import React from 'react';
import { Mail, Phone, Linkedin, Star, Globe, Plus, Sparkles } from 'lucide-react';

export function InfoRow({ label, val }) {
  return (
    <div>
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className="text-sm font-medium text-slate-900">{val || 'N/A'}</dd>
    </div>
  );
}

export function ContactsList({ contacts, onAdd, onEdit, onFindPICs }) {
  var items = [];
  var i;
  for (i = 0; i < contacts.length; i++) {
    items.push(
      <ContactItem key={contacts[i]._id} contact={contacts[i]} onEdit={onEdit} />
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Contacts ({contacts.length})</h3>
        <div className="flex gap-2">
          <button onClick={onFindPICs} className="flex items-center gap-2 px-3 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 text-sm" data-testid="contacts-tab-ai-btn"><Sparkles size={16} /> Find PICs</button>
          <button onClick={onAdd} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90" data-testid="add-contact-btn"><Plus size={18} /> Add Contact</button>
        </div>
      </div>
      <div className="space-y-3">
        {contacts.length === 0 && <p className="text-center py-8 text-slate-500">No contacts. Try AI to find key contacts.</p>}
        {items}
      </div>
    </div>
  );
}

function ContactItem({ contact, onEdit }) {
  var initials = contact.full_name ? contact.full_name.charAt(0).toUpperCase() : '?';
  var showSource = contact.source && contact.source !== 'manual';
  return (
    <div className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group" data-testid={'contact-' + contact._id}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold relative">
            {initials}
            {contact.is_primary && <Star size={12} className="absolute -top-1 -right-1 text-amber-500 fill-amber-500" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-slate-900">{contact.full_name}</h4>
              {contact.is_primary && <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Primary</span>}
              {showSource && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded capitalize">{contact.source}</span>}
            </div>
            <p className="text-sm text-slate-600">{contact.designation || 'N/A'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {contact.email && <a href={'mailto:' + contact.email} className="flex items-center gap-1 text-slate-600 hover:text-primary"><Mail size={14} /> {contact.email}</a>}
          {contact.phone && <span className="flex items-center gap-1 text-slate-600"><Phone size={14} /> {contact.phone}</span>}
          {contact.linkedin && <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600"><Linkedin size={14} /></a>}
          <button onClick={function() { onEdit(contact); }} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity">Edit</button>
        </div>
      </div>
    </div>
  );
}

export function DealsList({ deals, onAdd, onClickDeal, fmtCurrency }) {
  var items = [];
  var i;
  for (i = 0; i < deals.length; i++) {
    items.push(
      <DealItem key={deals[i]._id} deal={deals[i]} onClick={onClickDeal} fmtCurrency={fmtCurrency} />
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Deals ({deals.length})</h3>
        <button onClick={onAdd} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90" data-testid="new-deal-btn"><Plus size={18} /> New Deal</button>
      </div>
      <div className="space-y-3">
        {deals.length === 0 && <p className="text-center py-8 text-slate-500">No deals yet</p>}
        {items}
      </div>
    </div>
  );
}

function DealItem({ deal, onClick, fmtCurrency }) {
  return (
    <div onClick={function() { onClick(deal._id); }} className="p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
      <div className="flex items-center justify-between">
        <div><h4 className="font-medium text-slate-900">{deal.project_title}</h4><p className="text-sm text-slate-600 mt-1">{deal.deal_id}</p></div>
        <div className="text-right"><p className="font-semibold text-slate-900">{fmtCurrency(deal.total_value || 0)}</p><span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded mt-1 inline-block">{deal.stage}</span></div>
      </div>
    </div>
  );
}

export function ActivitiesList({ activities, fmtDate }) {
  var items = [];
  var i;
  for (i = 0; i < activities.length; i++) {
    var a = activities[i];
    items.push(
      <div key={a._id} className="p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2"><span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">{a.activity_type}</span><span className="text-sm text-slate-500">{fmtDate(a.date)}</span></div>
        <p className="font-medium text-slate-900">{a.subject}</p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Activities</h3>
      <div className="space-y-3">
        {activities.length === 0 && <p className="text-center py-8 text-slate-500">No activities logged</p>}
        {items}
      </div>
    </div>
  );
}

export function OverviewTab({ client }) {
  var prioColors = { High: 'bg-red-100 text-red-700', Medium: 'bg-yellow-100 text-yellow-700', Low: 'bg-green-100 text-green-700' };
  var statusColors = { 'Identified': 'bg-slate-100 text-slate-700', 'Researched': 'bg-blue-100 text-blue-700', 'Contacted': 'bg-indigo-100 text-indigo-700', 'In Discussion': 'bg-purple-100 text-purple-700', 'Proposal Sent': 'bg-amber-100 text-amber-700', 'Won': 'bg-green-100 text-green-700', 'Lost': 'bg-red-100 text-red-700', 'On Hold': 'bg-gray-100 text-gray-600' };
  var pc = prioColors[client.priority] || '';
  var sc = statusColors[client.prospecting_status] || 'bg-slate-100 text-slate-600';
  var services = client.relevant_services || [];
  var svcTags = [];
  for (var i = 0; i < services.length; i++) {
    svcTags.push(<span key={services[i]} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">{services[i]}</span>);
  }
  var wUrl = client.website ? (client.website.startsWith('http') ? client.website : 'https://' + client.website) : '';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Organization Details</h3>
          <dl className="space-y-3">
            <InfoRow label="Organization Type" val={client.organization_type} />
            <InfoRow label="Industry" val={client.industry} />
            <InfoRow label="Sector" val={client.sector} />
            <InfoRow label="Company Size" val={client.company_size} />
            <InfoRow label="Parent Entity" val={client.parent_entity} />
            {wUrl && <div><dt className="text-sm text-slate-600">Website</dt><dd className="text-sm"><a href={wUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1"><Globe size={14} /> {client.website}</a></dd></div>}
          </dl>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Prospecting</h3>
          <dl className="space-y-3">
            {client.priority && <div><dt className="text-sm text-slate-600">Priority</dt><dd><span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + pc}>{client.priority}</span></dd></div>}
            {client.prospecting_status && <div><dt className="text-sm text-slate-600">Pipeline Status</dt><dd><span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + sc}>{client.prospecting_status}</span></dd></div>}
            <div><dt className="text-sm text-slate-600">Relevant Services</dt><dd className="mt-1 flex flex-wrap gap-1">{svcTags.length > 0 ? svcTags : <span className="text-sm text-slate-400">None</span>}</dd></div>
          </dl>
          {client.address && <div className="mt-4"><p className="text-sm text-slate-600 font-medium mb-1">Address</p><p className="text-sm text-slate-600">{client.address}</p></div>}
        </div>
      </div>
      {client.notes && <div><h3 className="text-lg font-semibold text-slate-900 mb-2">Notes</h3><p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-lg whitespace-pre-wrap">{client.notes}</p></div>}
    </div>
  );
}
